import { Box, ButtonBase, useTheme } from "@mui/material";


import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {NOTIFICATION_COMPONENTS, FALLBACK_NOTIFICATION_COMPONENT} from "./NotificationMap";
import SidepanelLoader from "../../theme/sidepanel2/SidepanelLoader";
import { tokens } from "../../../theme";
import { ENV_TYPES } from "../Sidebar";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Text from "../../theme/text/Text";
import { format, isToday, isYesterday, parseISO,formatDistanceToNowStrict, subHours, isAfter } from 'date-fns';
import { useMarkNotificationAsReadMutation } from "../../../newapi/pwa/pwaSlice";
import { ImportExport } from "@mui/icons-material";

const SpInvoiceDetails = SidepanelLoader(() => import("../Sidepanels/SpInvoiceDetails/SpInvoiceDetails"));
const SpOrderDetails = SidepanelLoader(() => import("../Sidepanels/SpOrderDetails/SpOrderDetails"));
const SPStockDetails = SidepanelLoader(() => import("../Sidepanels/SpStockDetails/SPStockDetails"));
const SpLocationDetails = SidepanelLoader(() => import("../Sidepanels/SpLocationDetails/SpLocationDetails"));
const SpInboundDetails = SidepanelLoader(() => import("../Sidepanels/SpInboundDetails/SpInboundDetails"));
const SpReturnDetails = SidepanelLoader(() => import("../Sidepanels/SpReturnDetails/SpReturnDetails"));
const SpInvoiceProductDetails = SidepanelLoader(() => import("../Sidepanels/SpInvoiceProductDetails/SpInvoiceProductDetails"));
const SpWebshopCredentialsDetails = SidepanelLoader(() => import("../Sidepanels/SpWebshopCredentialsDetails/SpWebshopCredentialsDetails"));


function formatDateHeader(sentAt,t,i18n) {
    const date = parseISO(sentAt);
    if (isToday(date)) return t("today");
    if (isYesterday(date)) return t("yesterday");

    const localizedDate = date.toLocaleDateString(i18n.language, {
        month: 'long',
        day: 'numeric',
    });
    return `${localizedDate}`;
}

function getDisplayTime(sentAt, i18n, t) {
    const date = new Date(sentAt);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours < 24) {
        const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto' });
        return rtf.format(-Math.round(diffHours), 'hour'); 
    }

    // For dates older than 24h, show date at time:
    const localizedDate = date.toLocaleDateString(i18n.language, {
        month: 'short',
        day: 'numeric',
    });
    const localizedTime = date.toLocaleTimeString(i18n.language, {
        hour: 'numeric',
        minute: 'numeric',
    });
    const atText = t('at');

    return `${localizedDate} ${atText} ${localizedTime}`;
}

const NotificationsContent = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const {t,i18n} = useTranslation();
    const envType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const navigate = useNavigate();
    
    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isRelation = selectedEnvironmentType === ENV_TYPES[200];

    const [hasLoadedFromQuery, setHasLoadedFromQuery] = useState(false);

    const [orderId,setOrderId] = useState();
    const [invoiceId,setInvoiceId] = useState();
    const [invoiceProductId,setInvoiceProductId] = useState();
    const [productId,setProductId] = useState();
    const [locationId,setLocationId] = useState();
    const [inboundId,setInboundId] = useState();
    const [returnId,setReturnId] = useState();

    const [ordersSpIsExtended, setOrdersSpIsExtended] = useState(false);
    const [startLoadingOrderDetails, setStartLoadingOrderDetails] = useState();

    const [invoicesSpIsExtended, setInvoicesSpIsExtended] = useState(false);
    const [spInvoiceLoading, setSpInvoiceLoading] = useState();

    const [invoiceProductsSpIsExtended, setInvoiceProductsSpIsExtended] = useState(false);
    const [spInvoiceProductLoading, setSpInvoiceProductLoading] = useState();

    const [productsSpIsExtended, setProductsSpIsExtended] = useState(false);
    const [spProductLoading, setSpProductLoading] = useState();

    const [locationsSpIsExtended, setLocationsSpIsExtended] = useState(false);
    const [spLocationLoading, setSpLocationLoading] = useState();

    const [inboundsSpIsExtended, setInboundsSpIsExtended] = useState(false);
    const [spInboundLoading, setSpInboundLoading] = useState();

    const [returnsSpIsExtended, setReturnsSpIsExtended] = useState(false);
    const [spReturnLoading, setSpReturnLoading] = useState();

    const [webshopCredentialsSpIsExtended, setWebshopCredentialsSpIsExtended] = useState(false);
    const [spWebshopCredentialsLoading, setSpWebshopCredentialsLoading] = useState();
    const [webshopCredentialsId, setWebshopCredentialsId] = useState();

    const location = useLocation();
    const notifications = props.data?.notifications || [];
    const sorted = [...notifications].sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt));

    const [markNotificationAsRead, {isLoading}] = useMarkNotificationAsReadMutation();
    const currentUser = useSelector((state) => state.auth?.user || state.user?.currentUser);
    useEffect(() => {
        if (hasLoadedFromQuery) return; 

        const queryParams = new URLSearchParams(location.search);
        const type = queryParams.get('type');
        const id = queryParams.get('id');

        if (type === 'order_in_maintenance' || type === "orders_in_maintenance" || type === "order_in_backorder") {
            if(id){
                setOrderId(id); 
                if(!startLoadingOrderDetails){
                    setStartLoadingOrderDetails(true);
                } 
                setOrdersSpIsExtended(true);
                setHasLoadedFromQuery(true); // mark as run
            }
        } else if (type === "inbound_completed" || type === "inbound_incomplete_received") {
            if(id) {
                setInboundId(id); 
                if (!spInboundLoading) {
                    setSpInboundLoading(true);
                }
                setInboundsSpIsExtended(true);
                setHasLoadedFromQuery(true); // mark as run
            }
        }
    }, [location.search,hasLoadedFromQuery]);
    
    const handleResultClick = async (key,item) => {
        // Mark notification as read
        if (currentUser?.id && item?.id) {
            try {
                await markNotificationAsRead({
                    notificationId: item.id,
                    userId: currentUser.id
                }).unwrap();
            } catch (error) {
                console.error('Failed to mark notification as read:', error);
            }
        }

        setOrderId(null);
        setInvoiceId(null);
        setProductId(null);
        setLocationId(null);
        setInboundId(null);
        setReturnId(null);
        setWebshopCredentialsId(null);

        switch(key) {
            case "orders" :
                setOrderId(item.id); 
                if(!startLoadingOrderDetails){
                    setStartLoadingOrderDetails(true);
                } 
                setOrdersSpIsExtended(true);
                break;
            case "order_not_imported" : 
                window.location.href = item.data.url;
                break;
            case "stock_low" : 
                window.location.href = item.data.url;
                break;
            case "invoices" : 
                setInvoiceId(item.id); 
                if(!spInvoiceLoading){
                    setSpInvoiceLoading(true);
                }
                setInvoicesSpIsExtended(true);
                break;
            case "stock_difference" : 
                setProductId(item.body.externalId); 
                if(!spProductLoading) {
                    setSpProductLoading(true);
                } 
                setProductsSpIsExtended(true);
                break;
            case "locations" :
                setLocationId(item.id); 
                if(!spLocationLoading) {
                    setSpLocationLoading(true);
                } 
                setLocationsSpIsExtended(true);
                break;
            case "inbound_completed" :
            case "inbound_incomplete_received" :
                setInboundId(item.id);
                if (!spInboundLoading) {
                    setSpInboundLoading(true);
                }
                setInboundsSpIsExtended(true);
                break;
            case "returns" :
                setReturnId(item.id);
                if(!spReturnLoading){
                    setSpReturnLoading(true);
                }
                setReturnsSpIsExtended(true);
                break;
            case "return_processed" :
                setReturnId(item.data?.id ?? item.id);
                if(!spReturnLoading){
                    setSpReturnLoading(true);
                }
                setReturnsSpIsExtended(true);
                break;
            case "invoice_product_zero_price" :
                setInvoiceProductId(item.body.externalId); 
                if(!spReturnLoading){
                    setSpInvoiceProductLoading(true);
                } 
                setInvoiceProductsSpIsExtended(true);
                break;
            case "order_in_maintenance" : 
                setOrderId(item.data.id); 
                if(!startLoadingOrderDetails){
                    setStartLoadingOrderDetails(true);
                } 
                setOrdersSpIsExtended(true);
                break;
            case "orders_in_maintenance": 
                if(envType === ENV_TYPES[200]) {
                    window.location.href = "/relation/outbound?state=MAINTENANCE";
                }
                if(envType === ENV_TYPES[100]) {
                    window.location.href = "/warehouse/outbound/orders?state=MAINTENANCE";
                }
                if(props.onClose) {
                    props.onClose();
                }
                break;
            case "order_in_backorder": 
                setOrderId(item.data.id); 
                if(!startLoadingOrderDetails){
                    setStartLoadingOrderDetails(true);
                } 
                setOrdersSpIsExtended(true);
                break;
            case "orders_in_backorder":
                if(envType === ENV_TYPES[200]) {
                    window.location.href = "/relation/outbound?state=BACKORDER";
                    // navigate("relation/outbound?state=BACKORDER")
                }
                if(envType === ENV_TYPES[100]) {
                    window.location.href = "/warehouse/outbound/orders?state=BACKORDER";
                    // navigate("warehouse/outbound/orders?state=BACKORDER")
                }
                if(props.onClose) {
                    props.onClose();
                }
                break;
            case "system":
                // System notifications handle their own navigation
                break;
            case "export_ready":
                // Export ready notifications handle their own navigation
                break;
            case "webshop_approval_required":
                // If called from dropdown (has onClose prop), navigate to notifications page
                // If called from notifications page (no onClose prop), open sidepanel
                if(props.onClose) {
                    if(envType === ENV_TYPES[200]) {
                        window.location.href = "/relation/notifications";
                    } else {
                        window.location.href = "/warehouse/notifications";
                    }
                    props.onClose();
                } else {
                    const webshopId = item.data?.webshopCredentialsId || item.data?.id || item.body?.webshopCredentialsId || item.id;
                    setWebshopCredentialsId(webshopId);
                    if(!spWebshopCredentialsLoading) {
                        setSpWebshopCredentialsLoading(true);
                    }
                    setWebshopCredentialsSpIsExtended(true);
                }
                break;
            default :
                    // What drugs are u on mate?
                return;
        }
    }
    let lastDateHeader = null;

    return (
        <>
        
            {sorted?.map((item, index) => {
                const notificationType = item.type?.toLowerCase();
                const componentConfig = NOTIFICATION_COMPONENTS[notificationType] || FALLBACK_NOTIFICATION_COMPONENT;
                const { notifications_comp: Notification } = componentConfig;
                const currentDateHeader = formatDateHeader(item.sentAt,t,i18n);

                const showHeader = currentDateHeader !== lastDateHeader;
                lastDateHeader = currentDateHeader;

                 return (
                    <React.Fragment key={`notif-group-${index}`}>
                        {showHeader && (
                            <Box
                                sx={{
                                    ...props.compSx,
                                }}
                            >
                                <Text bold 
                                    sx={{ 
                                        mt: 2, 
                                        mb: 1, px: 1, 
                                        fontSize: 14, 
                                        color: 'text.secondary' 
                                    }}
                                >
                                    {currentDateHeader}
                                </Text>
                            </Box>
                        )}
                        <Box
                            key={"notif" + index}
                            sx={{
                                px: 1,
                                py: 1,
                                position: "relative",
                                display: "flex",
                                alignItems: "flex-start",
                                borderBottom:
                                    index !== notifications.length - 1
                                    ? `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`
                                    : null,
                                ...props.compSx,
                            }}
                        >
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Notification
                                    key={item.type + index}
                                    onClick={() => handleResultClick(item.type, item)}
                                    {...item}
                                />
                            </Box>
                            <Text light sx={{ flexShrink: 0, whiteSpace: "nowrap", pt: 1, pr: 1 }}>
                                {getDisplayTime(item.sentAt,i18n,t)}
                            </Text>
                        </Box>
                    </React.Fragment>
                )
            })}
            {(props.data?.remaining  > 0) &&
                <Box
                    sx={{
                        borderTop:`1px solid ${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0]}`,
                        top:-1,
                        position:'relative',
                        py:2,
                        pt:0,
                        px:2,
                    }}
                >
                    <ButtonBase
                        onClick={() => {
                            if(isRelation){
                                navigate("/relation/notifications");
                            }else{
                                navigate("/warehouse/notifications");
                            }
                            props.onClose();
                        }}  
                        sx={{
                            width:1,
                            px:1.5,
                            borderRadius:4,
                            height:32,
                            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                            textAlign:'center',
                            lineHeight:"32px",
                        }}
                    >
                        <Text>+ <b>{props.data?.remaining}</b>&nbsp; {t("other_notifications")}</Text>
                        
                    </ButtonBase>
                </Box>
            }

            {startLoadingOrderDetails && 
                <SpOrderDetails
                    isExtended={ordersSpIsExtended}
                    setIsExtended={setOrdersSpIsExtended}
                    orderId={orderId}
                    relation={envType === ENV_TYPES[200]}
                    canReserveOrderLines={envType === ENV_TYPES[200]}
                />
            }

            {spInvoiceLoading && 
                <SpInvoiceDetails
                    isExtended={invoicesSpIsExtended}
                    setIsExtended={setInvoicesSpIsExtended}
                    invoiceId={invoiceId}
                />
            }

            {spInvoiceProductLoading && 
                <SpInvoiceProductDetails
                    isExtended={invoiceProductsSpIsExtended}
                    setIsExtended={setInvoiceProductsSpIsExtended}
                    invoiceId={invoiceProductId}
                />
            }
            
            
            {spProductLoading && 
                <SPStockDetails
                    isExtended={productsSpIsExtended}
                    setIsExtended={setProductsSpIsExtended}
                    productId={productId}
                />
            }

            {/* spLocationLoading */}

            {spLocationLoading && 
                <SpLocationDetails
                    isExtended={locationsSpIsExtended}
                    setIsExtended={setLocationsSpIsExtended}
                    locationId={locationId}
                />
            }

            {spInboundLoading && 
                <SpInboundDetails
                    isExtended={inboundsSpIsExtended}
                    setIsExtended={setInboundsSpIsExtended}
                    inboundId={inboundId}
                />
            }

            {spReturnLoading &&
                <SpReturnDetails
                    isExtended={returnsSpIsExtended}
                    setIsExtended={setReturnsSpIsExtended}
                    returnId={returnId}
                />
            }

            {spWebshopCredentialsLoading &&
                <SpWebshopCredentialsDetails
                    isExtended={webshopCredentialsSpIsExtended}
                    setIsExtended={setWebshopCredentialsSpIsExtended}
                    webshopCredentialsId={webshopCredentialsId}
                />
            }

        </>
    )

}

export default NotificationsContent;